import axios, { AxiosError } from 'axios'
import type {
  QrSession,
  QrSessionStatus,
  TwoFactorSendResponse,
  TwoFactorVerifyResponse,
} from '@/models/qrLogin'
import type { ApiError } from '@/models/auth'
import { ENV } from '../env.js'

const API_URL = (ENV.backendUrl ?? 'http://localhost:4000') + '/api'

class QrLoginService {
  async createSession(): Promise<QrSession> {
    try {
      const response = await axios.post<QrSession>(`${API_URL}/portal/qr-login/create`)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'Failed to create QR session' }
    }
  }

  async getSessionStatus(sessionId: string): Promise<QrSessionStatus> {
    try {
      const response = await axios.get<QrSessionStatus>(
        `${API_URL}/portal/qr-login/${sessionId}/status`,
      )
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        throw error.response.data as ApiError
      }
      throw { error: 'Failed to get session status' }
    }
  }

  async send2FACode(
    sessionId: string,
    organizationId?: string,
  ): Promise<TwoFactorSendResponse> {
    try {
      const body: { session_id: string; organization_id?: string } = { session_id: sessionId }
      if (organizationId != null) {
        body.organization_id = organizationId
      }
      const response = await axios.post<TwoFactorSendResponse>(
        `${API_URL}/portal/qr-login/2fa/send`,
        body,
      )
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        if (error.response.status === 429) {
          throw { error: 'Too many requests. Please wait before trying again.', status: 429 }
        }
        throw error.response.data as ApiError
      }
      throw { error: 'Failed to send 2FA code' }
    }
  }

  async verify2FACode(sessionId: string, code: string): Promise<TwoFactorVerifyResponse> {
    try {
      const response = await axios.post<TwoFactorVerifyResponse>(
        `${API_URL}/portal/qr-login/2fa/verify`,
        { session_id: sessionId, code },
      )
      return response.data
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        if (error.response.status === 401) {
          throw { error: 'Invalid verification code', status: 401 }
        }
        if (error.response.status === 429) {
          throw { error: 'Too many attempts. Please wait before trying again.', status: 429 }
        }
        throw error.response.data as ApiError
      }
      throw { error: 'Failed to verify 2FA code' }
    }
  }

  generateQrCodeUrl(qrToken: string): string {
    return `kollapp://qr-login?token=${qrToken}`
  }
}

export default new QrLoginService()
